import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Download, Wallet, TrendingDown,
  TrendingUp, RefreshCw, Filter, MapPin,
} from "lucide-react";

const KIND_LABELS = {
  charge:  { label: "Lead charge", color: "text-rose-700", bg: "bg-rose-50",     border: "border-rose-200",     icon: TrendingDown },
  topup:   { label: "Wallet top-up", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", icon: TrendingUp },
  credit:  { label: "Refund credit", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", icon: RefreshCw },
  refund:  { label: "Refund", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", icon: RefreshCw },
};

const fmt$ = (cents) => {
  const sign = (cents || 0) < 0 ? "-" : "";
  const v = Math.abs(Number(cents || 0) / 100);
  return `${sign}$${v.toFixed(2)}`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/**
 * Shared billing-history view.
 *
 * @param mode 'company' | 'admin'
 *
 *   company → calls /api/marketplace/transactions, hides company filter, shows
 *     own wallet balance summary card.
 *   admin   → calls /api/admin/transactions, shows company filter dropdown
 *     populated from /api/admin/companies/lookup, hides wallet card (cross-co
 *     view doesn't have a single balance).
 */
export default function BillingHistory({ mode = "company" }) {
  const { user } = useAuth();
  const nav = useNavigate();

  const [dateFrom, setDateFrom] = useState(isoDaysAgo(30));
  const [dateTo, setDateTo] = useState(todayIso());
  const [kind, setKind] = useState("all");
  const [companyId, setCompanyId] = useState("all");
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Guard route
  useEffect(() => {
    if (!user) return;
    if (mode === "admin" && user.role !== "platform_admin") nav("/dashboard");
    if (mode === "company" && user.role === "platform_admin") nav("/admin/transactions");
  }, [user, mode, nav]);

  // Load company lookup for admin filter dropdown
  useEffect(() => {
    if (mode !== "admin") return;
    api.get("/admin/companies/lookup")
      .then(({ data }) => setCompanies(data.companies || []))
      .catch(() => {/* silent */});
  }, [mode]);

  // Load transactions
  useEffect(() => {
    setLoading(true);
    const url = mode === "admin" ? "/admin/transactions" : "/marketplace/transactions";
    const params = { date_from: dateFrom || undefined, date_to: dateTo || undefined };
    if (kind && kind !== "all") params.kind = kind;
    if (mode === "admin" && companyId && companyId !== "all") params.company_id = companyId;
    api.get(url, { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load transactions"))
      .finally(() => setLoading(false));
  }, [mode, dateFrom, dateTo, kind, companyId]);

  const transactions = data?.transactions || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return transactions;
    return transactions.filter((t) =>
      (t.lead?.first_name || "").toLowerCase().includes(q) ||
      (t.lead?.zip_code || "").toLowerCase().includes(q) ||
      (t.company?.business_name || "").toLowerCase().includes(q) ||
      (t.note || "").toLowerCase().includes(q),
    );
  }, [transactions, search]);

  const setRange = (days) => {
    setDateFrom(isoDaysAgo(days));
    setDateTo(todayIso());
  };

  const exportCsv = () => {
    if (!filtered.length) { toast.info("Nothing to export"); return; }
    const headers = mode === "admin"
      ? ["date", "company", "kind", "amount", "balance_after", "lead_name", "lead_zip", "lead_quote", "note", "lead_id", "assignment_id"]
      : ["date", "kind", "amount", "balance_after", "lead_name", "lead_zip", "lead_quote", "note", "lead_id", "assignment_id"];
    const rows = filtered.map((t) => {
      const base = [
        t.created_at,
        t.kind,
        (t.amount_cents / 100).toFixed(2),
        (t.balance_after_cents / 100).toFixed(2),
        `${t.lead?.first_name || ""} ${t.lead?.last_name || ""}`.trim(),
        t.lead?.zip_code || "",
        t.lead?.estimate_total != null ? "$" + Number(t.lead.estimate_total).toFixed(2) : "",
        t.note || "",
        t.lead_id || "",
        t.assignment_id || "",
      ];
      return mode === "admin" ? [t.created_at, t.company?.business_name || "", ...base.slice(1)] : base;
    });
    const csv = [headers, ...rows].map((r) =>
      r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${mode}-billing-${dateFrom}-to-${dateTo}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const charges = data?.summary?.by_kind?.charge || { count: 0, amount_cents: 0 };
  const topups = data?.summary?.by_kind?.topup || { count: 0, amount_cents: 0 };
  const credits = (data?.summary?.by_kind?.credit?.amount_cents || 0) + (data?.summary?.by_kind?.refund?.amount_cents || 0);
  const net = data?.summary?.net_cents || 0;

  const isAdmin = mode === "admin";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => nav(isAdmin ? "/admin" : "/dashboard")} data-testid="billing-history-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> {isAdmin ? "Admin" : "Dashboard"}
            </Button>
            <div>
              <div className="font-display font-bold text-slate-900">
                {isAdmin ? "Marketplace billing — all companies" : "Lead billing history"}
              </div>
              <div className="text-xs text-slate-500">
                {isAdmin ? "Every wallet movement across the platform" : "Charges, top-ups, and refunds on your wallet"}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {!isAdmin && (
            <Tile
              label="Wallet balance"
              value={fmt$(data?.balance_cents)}
              accent="text-[#C75D33]"
              icon={Wallet}
              testid="billing-stat-balance"
            />
          )}
          <Tile
            label="Charges in range"
            value={fmt$(Math.abs(charges.amount_cents))}
            sub={`${charges.count} lead${charges.count === 1 ? "" : "s"}`}
            accent="text-rose-600"
            icon={TrendingDown}
            testid="billing-stat-charges"
          />
          <Tile
            label="Top-ups in range"
            value={fmt$(topups.amount_cents)}
            sub={`${topups.count} top-up${topups.count === 1 ? "" : "s"}`}
            accent="text-emerald-600"
            icon={TrendingUp}
            testid="billing-stat-topups"
          />
          <Tile
            label="Credits / refunds"
            value={fmt$(credits)}
            accent="text-amber-600"
            icon={RefreshCw}
            testid="billing-stat-credits"
          />
          {isAdmin && (
            <Tile
              label="Net (range)"
              value={fmt$(net)}
              sub={net < 0 ? "Net debit" : "Net inflow"}
              accent={net < 0 ? "text-rose-600" : "text-emerald-600"}
              testid="billing-stat-net"
            />
          )}
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-5">
          <div className="flex items-center gap-2 mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">
            <Filter className="w-3.5 h-3.5" /> Filters
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                max={dateTo}
                className="h-10 rounded-xl mt-1.5"
                data-testid="billing-filter-from"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                min={dateFrom}
                max={todayIso()}
                className="h-10 rounded-xl mt-1.5"
                data-testid="billing-filter-to"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Kind</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="h-10 rounded-xl mt-1.5" data-testid="billing-filter-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All kinds</SelectItem>
                  <SelectItem value="charge">Lead charges</SelectItem>
                  <SelectItem value="topup">Wallet top-ups</SelectItem>
                  <SelectItem value="credit">Credits</SelectItem>
                  <SelectItem value="refund">Refunds</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Company</Label>
                <Select value={companyId} onValueChange={setCompanyId}>
                  <SelectTrigger className="h-10 rounded-xl mt-1.5" data-testid="billing-filter-company">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="all">All companies</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`billing-filter-company-opt-${c.id}`}>
                        {c.business_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Search</Label>
              <Input
                placeholder="Name, zip, note…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-10 rounded-xl mt-1.5"
                data-testid="billing-filter-search"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
              {[7, 30, 90, 365].map((d) => (
                <button
                  key={d}
                  onClick={() => setRange(d)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-100`}
                  data-testid={`billing-range-${d}`}
                >
                  Last {d}d
                </button>
              ))}
            </div>
            <Button variant="outline" className="rounded-xl h-9 ml-auto" onClick={exportCsv} disabled={!filtered.length} data-testid="billing-export-btn">
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Date</th>
                  {isAdmin && <th className="text-left px-5 py-3 font-bold">Company</th>}
                  <th className="text-left px-5 py-3 font-bold">Kind</th>
                  <th className="text-left px-5 py-3 font-bold">Lead</th>
                  <th className="text-right px-5 py-3 font-bold">Lead quote</th>
                  <th className="text-right px-5 py-3 font-bold">Amount</th>
                  <th className="text-right px-5 py-3 font-bold">Balance after</th>
                  <th className="text-left px-5 py-3 font-bold">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={isAdmin ? 8 : 7} className="px-6 py-16 text-center text-slate-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 8 : 7} className="px-6 py-16 text-center text-slate-500" data-testid="billing-empty">
                    {transactions.length === 0
                      ? "No transactions in this date range."
                      : "No matches for your search."}
                  </td></tr>
                ) : filtered.map((t) => {
                  const m = KIND_LABELS[t.kind] || KIND_LABELS.charge;
                  const Icon = m.icon;
                  const isNegative = (t.amount_cents || 0) < 0;
                  return (
                    <tr key={t.id} className="hover:bg-slate-50" data-testid={`billing-row-${t.id}`}>
                      <td className="px-5 py-3 text-slate-700 whitespace-nowrap">
                        <div className="font-semibold text-slate-900">{new Date(t.created_at).toLocaleDateString()}</div>
                        <div className="text-xs text-slate-500">{new Date(t.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                      </td>
                      {isAdmin && (
                        <td className="px-5 py-3 text-slate-700">
                          <div className="font-semibold text-slate-900">{t.company?.business_name || "—"}</div>
                          <div className="text-xs text-slate-400">{t.company?.slug || ""}</div>
                        </td>
                      )}
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full ${m.bg} ${m.color} border ${m.border}`}>
                          <Icon className="w-3 h-3" /> {m.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-700">
                        {t.lead ? (
                          <>
                            <div className="font-semibold text-slate-900">{t.lead.first_name} {t.lead.last_name || ""}</div>
                            <div className="text-xs text-slate-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{t.lead.zip_code || "—"}</div>
                          </>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-700">
                        {t.lead?.estimate_total != null ? `$${Number(t.lead.estimate_total).toFixed(0)}` : <span className="text-slate-400">—</span>}
                      </td>
                      <td className={`px-5 py-3 text-right font-bold tabular-nums ${isNegative ? "text-rose-700" : "text-emerald-700"}`}>
                        {fmt$(t.amount_cents)}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-900 font-semibold">{fmt$(t.balance_after_cents)}</td>
                      <td className="px-5 py-3 text-xs text-slate-600 max-w-[280px] truncate" title={t.note || ""}>{t.note || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
              {filtered.length > 0 && (
                <tfoot className="bg-slate-50/50 text-xs text-slate-500">
                  <tr>
                    <td colSpan={isAdmin ? 8 : 7} className="px-5 py-3">
                      Showing {filtered.length} of {data?.total ?? 0} transactions in range
                      {data?.total > filtered.length && search && <> · clear search to see all</>}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function Tile({ label, value, sub, accent = "text-slate-900", icon: Icon, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`font-display text-2xl font-bold mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
